import { useState } from "react";
import { Link } from "wouter";
import { trpc } from "../../lib/trpc";
import { useAuth } from "../../contexts/AuthContext";
import { useLanguage } from "../../contexts/LanguageContext";
import { fmtCurrency, fmtDateTime } from "../../lib/format";
import { OrderStatusBadge } from "../../components/order/OrderStatusBadge";
import { SettlementStatusBadge } from "../../components/settlement/SettlementStatusBadge";
import { SERVICE_TYPE_META, type Currency } from "../../../../shared/const";

/**
 * 每月訂單（A18；Phase 7）— agency（帳單管理）與 fleet（應收帳款）共用。
 * 依月份分組：月卡（訂單數/completed 小計/帳單狀態與下載）＋展開當月訂單。
 * 金額可見度：agency 只見 finalPrice、fleet 只見 fleetPayout（server 已裁剪，另側為 null）。
 */
function monthTitle(key: string, lang: string): string {
  const [y, m] = key.split("-");
  return lang === "en-US" ? `${y}-${m}` : `${y} 年 ${Number(m)} 月`;
}

export default function MonthlyStatements() {
  const { user } = useAuth();
  const { t, lang } = useLanguage();
  const [open, setOpen] = useState<string | null>(null);
  const q = trpc.settlement.myMonthly.useQuery();
  const isAgency = user?.role === "agency";

  if (q.isLoading) return <div className="h-24 animate-pulse rounded-lg bg-muted/40" />;
  const groups = q.data ?? [];
  if (groups.length === 0) {
    return (
      <p className="p-6 text-[13px] text-muted-foreground">
        {t({ "zh-TW": "尚無訂單。", "en-US": "No orders yet.", "zh-CN": "尚无订单。" })}
      </p>
    );
  }

  return (
    <div className="space-y-3">
      {groups.map((g) => (
        <div key={g.monthKey} className="rounded-lg border border-border">
          <button
            type="button"
            className="flex w-full flex-wrap items-center justify-between gap-2 p-4 text-left"
            onClick={() => setOpen(open === g.monthKey ? null : g.monthKey)}
          >
            <div className="flex items-center gap-3">
              <span className="text-[15px] font-semibold">{monthTitle(g.monthKey, lang)}</span>
              <span className="text-[12px] text-muted-foreground">
                {t({ "zh-TW": "訂單", "en-US": "orders", "zh-CN": "订单" })} {g.orders.length}
                {" · "}
                {t({ "zh-TW": "已完成", "en-US": "completed", "zh-CN": "已完成" })} {g.completedCount}
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              {g.subtotal != null && g.currency != null && (
                <span className="text-[14px] font-medium">
                  {isAgency
                    ? t({ "zh-TW": "應付", "en-US": "Payable", "zh-CN": "应付" })
                    : t({ "zh-TW": "實收", "en-US": "Receivable", "zh-CN": "实收" })}{" "}
                  {fmtCurrency(g.subtotal, g.currency as Currency)}
                </span>
              )}
              {g.statements.map((s) => (
                <span key={s.id} className="flex items-center gap-1">
                  <SettlementStatusBadge status={s.status} />
                  <a
                    className="text-[12px] underline"
                    href={`/api/settlements/${s.id}.xlsx`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    xlsx
                  </a>
                  <a
                    className="text-[12px] underline"
                    href={`/api/settlements/${s.id}.pdf`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    PDF
                  </a>
                </span>
              ))}
            </div>
          </button>
          {open === g.monthKey && (
            <div className="border-t border-border">
              {g.orders.map((o) => (
                <Link
                  key={o.id}
                  href={`/orders/${o.id}`}
                  className="flex flex-wrap items-center justify-between gap-2 border-b border-border p-3 text-[13px] last:border-b-0 hover:bg-muted/30"
                >
                  <span className="flex flex-wrap items-center gap-2">
                    <span className="font-mono text-[12px]">{o.orderNumber}</span>
                    <span className="text-muted-foreground">{fmtDateTime(o.pickupTime, lang)}</span>
                    <span>{t(SERVICE_TYPE_META[o.serviceType].label)}</span>
                  </span>
                  <span className="flex items-center gap-2">
                    <OrderStatusBadge status={o.status} size="sm" />
                    <span className="font-medium">
                      {isAgency
                        ? fmtCurrency(o.finalPrice, o.currency)
                        : fmtCurrency(o.fleetPayout, o.fleetPayoutCurrency ?? "HKD")}
                    </span>
                  </span>
                </Link>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
